<template>
  <div
    v-if="pageReady"
    ref="searchBar"
    class="flex items-center justify-between flex-wrap md:flex-nowrap mx-auto bg-white rounded-none p-3 border-none md:border border-neutral-100 shadow-lg"
  >
    <div
      class="cursor-pointer block md:hidden w-full p-4 rounded-lg bg-neutral-100 hover:bg-neutral-200 flex items-center justify-between text-neutral-600 focus:outline-none focus:bg-neutral-200"
      tabindex="0"
      aria-hidden="true"
      @click="toggleDayFilter"
      @keypress.space="toggleDayFilter"
    >
      <div>
        <p>
          <fa
            :icon="['fal', 'calendar-day']"
            class="mr-1"
          />
          {{ displayDateShort }} from {{ displayTimeFrame }}
        </p>
      </div>
      <fa
        :icon="['fal', 'angle-right']"
        class="text-2xl"
      />
    </div>
    <div class="flex items-start md:items-center md:space-x-1 lg:space-x-4">
      <div class="group md:relative">
        <vetster-search-input
          :visible="shouldShowDayFilter"
          :filters-selected="hasFiltersSelected"
          :label="$t('app.date')"
          @toggle="toggleDayFilter"
        >
          {{ displayDateShort }}
        </vetster-search-input>

        <div
          v-show="shouldShowDayFilter"
          ref="dayFilter"
          class="search-panel left-0"
        >
          <vetster-mobile-search-nav @close="closeFilters" />

          <div class="p-4">
            <p class="vetster-mobile-info">
              {{ $t('search.what-day-would-you-like-to-see-someone') }}
            </p>
            <vetster-search-day-picker
              @date-selected="dateChanged"
              @close="closeFilters"
            />

            <div
              v-if="hasTime"
              class="flex justify-end mt-8 block md:hidden"
              aria-hidden="true"
            >
              <vetster-button-legacy
                variant="bright"
                class="w-full mt-8"
                size="lg"
                @click="toggleTimeFilter"
              >
                {{ $t('app.continue') }}
              </vetster-button-legacy>
            </div>
          </div>
        </div>
      </div>
      <div class="group md:relative">
        <vetster-search-input
          :visible="shouldShowTimeFilter"
          :filters-selected="hasFiltersSelected"
          :label="$t('search.desired-time')"
          @toggle="toggleTimeFilter"
        >
          {{ displayTimeFrame }}
        </vetster-search-input>

        <div
          v-show="shouldShowTimeFilter"
          ref="timeFilter"
          class="search-panel left-0"
        >
          <vetster-mobile-search-nav @close="closeFilters" />

          <div class="p-4">
            <p class="vetster-mobile-info">
              {{ $t('search.what-time-of-day-works-best') }}
            </p>

            <vetster-search-time-picker
              @close="closeFilters"
              @time-selected="timeChanged"
            />

            <div
              v-if="hasDate"
              class="flex justify-end mt-8 block md:hidden"
              aria-hidden="true"
            >
              <vetster-button-legacy
                variant="bright"
                size="lg"
                class="w-full mt-8"
                @click="toggleLocationFilter"
              >
                {{ $t('app.continue') }}
              </vetster-button-legacy>
            </div>
          </div>
        </div>
      </div>
      <div class="group md:relative">
        <vetster-search-input
          :visible="shouldShowLocationFilter"
          :filters-selected="hasFiltersSelected"
          :label="$t('search.your-location')"
          @toggle="toggleLocationFilter"
        >
          {{ displayCity }}
        </vetster-search-input>

        <!-- Intentional v-show here so we dont reload the mapbox JS -->
        <div
          v-show="shouldShowLocationFilter"
          ref="locationFilter"
          class="search-panel left-0"
        >
          <vetster-mobile-search-nav @close="closeFilters" />

          <div class="p-4">
            <p class="vetster-mobile-info">
              {{ $t('search.where-are-you-located') }}
            </p>

            <vetster-search-location-picker
              :locale="locale"
              :countries="country"
              :location="displayCity"
              @close="closeFilters"
              @location-selected="locationChanged"
            />

            <div
              class="flex justify-end mt-8 block md:hidden"
              aria-hidden="true"
            >
              <vetster-button-legacy
                variant="bright"
                size="lg"
                class="w-full mt-8"
                :disabled="!hasLocation"
                @click="toggleAdditionalFilters"
              >
                {{ $t('app.continue') }}
              </vetster-button-legacy>
            </div>
          </div>
        </div>
      </div>
      <div class="group md:relative">
        <vetster-search-input
          :visible="shouldShowAdditionalFilters"
          :filters-selected="hasFiltersSelected"
          :label="$t('search.more')"
          @toggle="toggleAdditionalFilters"
        >
          <span :class="{ 'text-pink-600': hasAdditionalFilters }">
            {{ $t('search.additional-filters') }}
          </span>
        </vetster-search-input>

        <div
          v-show="shouldShowAdditionalFilters"
          ref="additionalFilters"
          class="search-panel left-0 md:left-auto md:right-0 overflow-auto"
        >
          <vetster-mobile-search-nav @close="closeFilters" />

          <div class="p-4">
            <p class="vetster-mobile-info mb-4">
              {{ $t('search.any-other-requests') }}
            </p>

            <vetster-search-language-input
              :expanded="true"
              :disabled="loading"
            />
            <vetster-areas-of-interest-input :disabled="loading" />
            <vetster-species-treated-input
              :expanded="true"
              :disabled="loading"
            />
            <div
              class="flex justify-end mt-8 block md:hidden"
              aria-hidden="true"
            >
              <vetster-button-legacy
                variant="bright"
                size="lg"
                class="w-full mt-8"
                @click="search"
              >
                {{ $t('app.continue') }}
              </vetster-button-legacy>
            </div>
          </div>
        </div>
      </div>
    </div>

    <vetster-button-legacy
      variant="bright"
      size="lg"
      class="hidden md:block w-full md:w-auto mt-4 md:mt-0 flex items-center justify-center transition duration-500 ml-0 md:ml-8"
      aria-hidden="true"
      @click="search"
    >
      <fa
        class="fa-fw mx-2 text-xl"
        :icon="['fal', 'search']"
      />
    </vetster-button-legacy>
  </div>
</template>

<script>
import size from 'lodash/size';
import { DateTime } from 'luxon';
import { mapGetters, mapState } from 'vuex';

import VetsterAreasOfInterestInput from '@/components/VetsterAreasOfInterestInput';
import VetsterMobileSearchNav from '@/components/VetsterMobileSearchNav';
import VetsterSearchDayPicker from '@/components/VetsterSearchDayPicker';
import VetsterSearchInput from '@/components/VetsterSearchInput';
import VetsterSearchLanguageInput from '@/components/VetsterSearchLanguageInput';
import VetsterSearchLocationPicker from '@/components/VetsterSearchLocationPicker';
import VetsterSearchTimePicker from '@/components/VetsterSearchTimePicker';
import VetsterSpeciesTreatedInput from '@/components/VetsterSpeciesTreatedInput';
import useSmartTime from '@/composables/use-smart-time';
import ClickAway from '@/mixins/ClickAway';

// TODO: Need to make sure tab accessibility works on default input states
// TODO: Mobile has not been looked at yet
// TODO: Need to focus city search typeahead on menu open

export default {
  name: 'VetsterSearchBar',

  components: {
    VetsterAreasOfInterestInput,
    VetsterMobileSearchNav,
    VetsterSearchDayPicker,
    VetsterSearchInput,
    VetsterSearchLanguageInput,
    VetsterSearchLocationPicker,
    VetsterSearchTimePicker,
    VetsterSpeciesTreatedInput,
  },

  mixins: [ClickAway],

  props: {
    country: {
      type: String,
      required: true,
    },
    filters: {
      type: [Object, Array],
      default: () => ({}),
    },
    locale: {
      type: String,
      default: null,
    },
    logoPath: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isGlobalUser: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { convertTo12HourTime } = useSmartTime();

    return {
      convertTo12HourTime,
    };
  },

  data() {
    return {
      shouldShowAdditionalFilters: false,
      shouldShowDayFilter: true,
      shouldShowLocationFilter: false,
      shouldShowTimeFilter: false,
      shouldShowTimezones: false,
      timezone: DateTime.local().zoneName,
      pageReady: false,
    };
  },

  computed: {
    ...mapState('search', ['inputs']),

    ...mapGetters('search', [
      'displayCity',
      'displayDate',
      'displayDateShort',
    ]),

    displayTimeFrame() {
      return `${this.convertTo12HourTime(
        this.inputs.time_from,
      )} to ${this.convertTo12HourTime(this.inputs.time_to)}`;
    },

    hasLocation() {
      return this.inputs.city && this.inputs.province && this.inputs.country;
    },

    hasTime() {
      return this.inputs.time_from >= 0 && this.inputs.time_to >= 0;
    },

    hasDate() {
      return !!this.inputs.date_from;
    },

    hasDesignation() {
      return !!this.inputs.designation;
    },

    canSearch() {
      return (
        this.hasLocation && this.hasTime && this.hasDate && this.hasDesignation
      );
    },

    hasAdditionalFilters() {
      return this.inputs.language !== 'en' || size(this.inputs.species_treated) || size(this.inputs.areas_of_interest);
    },

    hasFiltersSelected() {
      return (
        this.shouldShowTimeFilter
        || this.shouldShowDayFilter
        || this.shouldShowLocationFilter
        || this.shouldShowAdditionalFilters
      );
    },
  },

  async created() {
    await this.$store.commit('setIsGlobalUser', this.isGlobalUser);

    document.addEventListener('click', this.registerClickAway);

    this.$nextTick(() => {
      this.pageReady = true;
    });
  },

  destroyed() {
    this.$bus.$off('escape', this.closeFilters);
  },

  methods: {
    dateChanged() {
      this.closeFilters();
      this.shouldShowTimeFilter = true;
    },

    timeChanged() {
      this.closeFilters();
      this.shouldShowLocationFilter = true;
    },

    locationChanged() {
      this.closeFilters();
      this.shouldShowAdditionalFilters = true;
    },

    closeFilters() {
      this.shouldShowLocationFilter = false;
      this.shouldShowDayFilter = false;
      this.shouldShowTimeFilter = false;
      this.shouldShowAdditionalFilters = false;
    },

    registerClickAway(event) {
      this.hideOnClickAway(event, this.$refs.searchBar, this.closeFilters, [
        this.$refs.dayFilter,
        this.$refs.timeFilter,
        this.$refs.locationFilter,
        this.$refs.additionalFilters,
      ]);
    },

    toggleAdditionalFilters() {
      this.closeFilters();
      this.shouldShowAdditionalFilters = !this.shouldShowAdditionalFilters;
    },

    toggleDayFilter() {
      this.closeFilters();
      this.shouldShowDayFilter = !this.shouldShowDayFilter;
    },

    toggleTimeFilter() {
      this.closeFilters();
      this.shouldShowTimeFilter = !this.shouldShowTimeFilter;
    },

    toggleLocationFilter() {
      this.closeFilters();
      this.shouldShowLocationFilter = !this.shouldShowLocationFilter;
    },

    search() {
      this.closeFilters();
      this.$emit('search');
    },
  },
};
</script>

<style scoped>
.search-panel {
  @apply ml-0 md:-ml-1 fixed top-0 md:top-auto md:mt-0 md:absolute w-full h-full md:h-auto md:w-96 bg-white md:rounded-lg md:border md:border-neutral-100 md:shadow-lg md:mt-7 z-50;
}
</style>
